/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * useNotificationHistory Hook
 *
 * Provides access to read notifications history for the current user.
 * Supports both client and therapist views.
 *
 * Features:
 * - Fetches read notifications from last 30 days
 * - Separate RPC calls for clients vs therapists
 * - Auto-refetch when drawer opens
 * - Loading and error states
 *
 * Usage:
 * const { notifications, isLoading, error, refetch } = useNotificationHistory({
 *   userRole: 'client',
 *   enabled: true
 * });
 */

import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { logger } from "@/lib/logger";

interface HistoryNotification {
  id: string;
  type: "comment" | "note";
  taskId: string;
  taskTitle: string;
  journeyTitle?: string;
  preview: string;
  timestamp: string;
  readAt?: string;
  viewedAt?: string;
  authorName?: string;
  clientName?: string;
  clientId?: string;
  isPromptResponse?: boolean;
  actionType?: string;
}

interface UseNotificationHistoryProps {
  userRole: "client" | "therapist";
  enabled?: boolean;
  daysBack?: number;
}

export const useNotificationHistory = ({
  userRole,
  enabled = true,
  daysBack = 30,
}: UseNotificationHistoryProps) => {
  const { user } = useAuth();

  const {
    data: notifications = [],
    isLoading,
    error,
    refetch,
  } = useQuery<HistoryNotification[]>({
    queryKey: ["notification-history", user?.id, userRole, daysBack],
    queryFn: async () => {
      if (!user) return [];

      try {
        if (userRole === "client") {
          // Fetch client's read comments
          const { data, error } = await supabase.rpc(
            "get_read_notifications_for_client",
            {
              target_user_id: user.id,
              days_back: daysBack,
            }
          );

          if (error) throw error;

          logger.info(
            "[useNotificationHistory] Fetched client history:",
            data?.length
          );

          // Map to notification format
          const mappedNotifications: HistoryNotification[] = (data || []).map(
            (item: any) => ({
              id: item.comment_id,
              type: "comment" as const,
              taskId: item.task_id,
              taskTitle: item.task_title,
              preview: item.comment_text,
              timestamp: item.created_at,
              readAt: item.read_at,
              authorName: item.author_name,
            })
          );

          return mappedNotifications;
        } else {
          // Fetch therapist's viewed notes
          const { data, error } = await supabase.rpc(
            "get_read_notifications_for_therapist",
            {
              target_therapist_id: user.id,
              days_back: daysBack,
            }
          );

          if (error) throw error;

          logger.info(
            "[useNotificationHistory] Fetched therapist history:",
            data?.length
          );

          // Map to notification format
          const mappedNotifications: HistoryNotification[] = (data || []).map(
            (item: any) => ({
              id: item.notification_id,
              type: "note" as const,
              taskId: item.task_id,
              taskTitle: item.task_title,
              journeyTitle: item.journey_title,
              preview: item.preview_text,
              timestamp: item.updated_at,
              viewedAt: item.viewed_at,
              clientName: item.client_name,
              clientId: item.client_id,
              isPromptResponse: item.is_prompt_response,
            })
          );

          return mappedNotifications;
        }
      } catch (error) {
        logger.error("[useNotificationHistory] Error fetching history:", error);
        throw error;
      }
    },
    enabled: enabled && !!user,
    staleTime: 30000, // 30 seconds
    refetchOnWindowFocus: false, // Don't refetch on window focus for history
  });

  return {
    notifications,
    isLoading,
    error,
    refetch,
  };
};
